/**
 * OrderReview.js — Order summary + Checkout (kind "order-review").
 *
 * Parity with old showOrderDetails (main.js): bot intro + card with
 * Edit Address, buyer rows (N/A when empty), product cards, totals
 * (Shipping/taxes/fees "Calculated at checkout"), free-shipping note,
 * redirect hint, and Checkout → Shopify new tab.
 *
 * message.data: { type: "ship"|"pickup", cart: <BE cart>, details: {...} }
 */

import { emit } from "../../../core/state.js";
import { getTenantId } from "../../../core/configAccessors.js";
import { syncCartWithWebsite } from "../../../features/optional/cart.js";

function isWineEngineTenant() {
  const id = String(getTenantId() || "").toLowerCase();
  return id === "wineengine" || id === "thewineengine";
}

function cleanDisplay(value) {
  if (value == null) return "";
  const text = String(value).trim();
  return text === "undefined" || text === "null" ? "" : text;
}

function displayOrNA(value) {
  return cleanDisplay(value) || "N/A";
}

const EDIT_ICON = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" fill="currentColor"/><path d="M20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z" fill="currentColor"/></svg>`;

// Wine Engine / old main.js ship rows (no phone, company, state).
const WINEENGINE_SHIP_ROWS = [
  ["First name", "firstName"],
  ["Last name", "lastName"],
  ["Email", "email"],
  ["Address", "address1"],
  ["City", "city"],
  ["Postcode", "zip"],
  ["Country", "country"],
];

const DEFAULT_SHIP_ROWS = [
  ["First name", "firstName"],
  ["Last name", "lastName"],
  ["Email", "email"],
  ["Phone", "phone"],
  ["Company", "company"],
  ["Address", "address1"],
  ["City", "city"],
  ["State", "province"],
  ["ZIP", "zip"],
  ["Country", "country"],
];

const PICKUP_ROWS = [["Email", "email"]];

function moneySymbol(currencyCode) {
  return { USD: "$", GBP: "£", EUR: "€", INR: "₹" }[currencyCode] || "";
}

export default class OrderReview {
  render(message) {
    const type = message?.data?.type === "pickup" ? "pickup" : "ship";
    const cart = message?.data?.cart || {};
    const details = message?.data?.details || {};
    const items = Array.isArray(cart.items) ? cart.items : [];
    const summary = cart.cart || cart.summary || {};
    const checkoutUrl = summary.checkoutUrl || cart.checkoutUrl || "";
    const wineEngine = isWineEngineTenant();

    const wrap = document.createElement("div");
    wrap.className = "ws-order-review";

    // ── Header: Order Summary + Edit Address ──
    const head = document.createElement("div");
    head.className = "ws-order-head";
    const title = document.createElement("p");
    title.className = "ws-order-title";
    title.textContent = "Order Summary";
    head.appendChild(title);

    const edit = document.createElement("button");
    edit.type = "button";
    edit.className = "ws-order-edit";
    edit.innerHTML = `${EDIT_ICON}<span>Edit Address</span>`;
    edit.addEventListener("click", () =>
      emit("ws-checkout-edit", { type, values: details }),
    );
    head.appendChild(edit);
    wrap.appendChild(head);

    const divider = document.createElement("hr");
    divider.className = "ws-order-divider";
    wrap.appendChild(divider);

    // ── Buyer details (always show rows; empty → N/A like old) ──
    const rows =
      type === "ship"
        ? wineEngine
          ? WINEENGINE_SHIP_ROWS
          : DEFAULT_SHIP_ROWS
        : PICKUP_ROWS;
    const detailWrap = document.createElement("div");
    detailWrap.className = "ws-order-details";
    rows.forEach(([label, key]) => {
      // Optional Fogarty-only fields: skip when empty.
      if (
        !wineEngine &&
        (key === "phone" || key === "company" || key === "province") &&
        !cleanDisplay(details[key])
      ) {
        return;
      }
      let value;
      if (key === "address1") {
        const joined = [details.address1, details.address2]
          .map(cleanDisplay)
          .filter(Boolean)
          .join(" ");
        value = displayOrNA(joined);
      } else {
        value = displayOrNA(details[key]);
      }
      const row = document.createElement("div");
      row.className = "ws-order-row";
      const l = document.createElement("div");
      l.className = "ws-order-row-label";
      l.textContent = `${label}:`;
      const v = document.createElement("div");
      v.className = "ws-order-row-value";
      v.textContent = value;
      row.appendChild(l);
      row.appendChild(v);
      detailWrap.appendChild(row);
    });
    wrap.appendChild(detailWrap);

    // ── Line items (cart-product cards) ──
    items.forEach((p) => {
      wrap.appendChild(this._productCard(p));
    });

    // ── Totals ──
    const totals = document.createElement("div");
    totals.className = "ws-order-totals";

    const hr1 = document.createElement("hr");
    hr1.className = "ws-order-divider";
    totals.appendChild(hr1);

    const table = document.createElement("div");
    table.className = "ws-order-totals-table";

    const subTotalCell = summary.memberDiscount
      ? `<s class="ws-order-strike">${summary.originalSubTotal || ""}</s> ${summary.subTotal || ""}`
      : summary.subTotal || "";
    table.appendChild(this._totalRow("Subtotal", subTotalCell, { html: true }));

    if (summary.memberDiscount) {
      table.appendChild(
        this._totalRow("Member discount", `−${summary.memberDiscount}`, {
          accent: true,
        }),
      );
    }

    if (type !== "pickup") {
      table.appendChild(this._shippingRow(summary));
    }
    table.appendChild(
      this._totalRow("Estimated taxes", "Calculated at checkout", {
        muted: true,
      }),
    );
    table.appendChild(
      this._totalRow("Order fees", "Calculated at checkout", { muted: true }),
    );
    totals.appendChild(table);

    const hr2 = document.createElement("hr");
    hr2.className = "ws-order-divider";
    totals.appendChild(hr2);

    const est = document.createElement("div");
    est.className = "ws-order-estimated";
    const estLabel = document.createElement("div");
    estLabel.innerHTML =
      'Estimated total <span class="ws-order-estimated-hint">(before tax &amp; fees)</span>';
    const estVal = document.createElement("div");
    estVal.textContent = summary.total || "";
    est.appendChild(estLabel);
    est.appendChild(estVal);
    totals.appendChild(est);

    if (type !== "pickup") {
      if (summary.freeShippingRemaining) {
        const note = document.createElement("p");
        note.className = "ws-order-shipping-note";
        note.textContent = `🚚 Spend ${summary.freeShippingRemaining} more and get free shipping`;
        totals.appendChild(note);
      } else if (summary.freeShippingUnlocked) {
        const note = document.createElement("p");
        note.className = "ws-order-shipping-note";
        note.textContent = "🚚 You've unlocked free shipping!";
        totals.appendChild(note);
      }
    }

    const redirect = document.createElement("p");
    redirect.className = "ws-order-redirect";
    redirect.textContent = "You'll be redirected to checkout page";
    totals.appendChild(redirect);

    wrap.appendChild(totals);

    // ── Checkout CTA ──
    const footer = document.createElement("div");
    footer.className = "ws-order-footer";
    const checkout = document.createElement("button");
    checkout.type = "button";
    checkout.className = "ws-order-checkout-btn";
    checkout.textContent = "Checkout";
    checkout.disabled = !checkoutUrl;
    checkout.addEventListener("click", () => {
      if (!checkoutUrl) return;
      window.open(checkoutUrl, "_blank", "noopener");
      // Wine Engine: after leaving for Shopify checkout, reload host page
      // so the site cart stays in sync (not on Continue / address submit).
      if (wineEngine) syncCartWithWebsite();
    });
    footer.appendChild(checkout);
    wrap.appendChild(footer);

    return wrap;
  }

  _productCard(p) {
    const line = document.createElement("div");
    line.className = "ws-order-item";

    if (p.image) {
      const imgWrap = document.createElement("div");
      imgWrap.className = "ws-order-item-img-wrap";
      const img = document.createElement("img");
      img.className = "ws-order-item-img";
      img.src = p.image;
      img.alt = p.productTitle || "Item";
      img.loading = "lazy";
      imgWrap.appendChild(img);
      line.appendChild(imgWrap);
    }

    const body = document.createElement("div");
    body.className = "ws-order-item-body";

    const t = document.createElement("strong");
    t.className = "ws-order-item-title";
    t.textContent = p.productTitle || "";
    body.appendChild(t);

    const desc = document.createElement("p");
    desc.className = "ws-order-item-desc";
    if (p.isDiscounted) {
      desc.innerHTML = `<strong>${p.price || ""}</strong> <s class="ws-order-strike">${p.originalPrice || ""}</s> per bottle <span class="ws-order-member-price">Member price</span>`;
    } else {
      desc.textContent = `${p.price || ""} per bottle`;
    }
    body.appendChild(desc);

    const footer = document.createElement("div");
    footer.className = "ws-order-item-footer";
    const qty = document.createElement("p");
    qty.className = "ws-order-item-qty";
    qty.textContent = `${p.price || ""} X ${p.quantity ?? 1}`;
    const price = document.createElement("p");
    price.className = "ws-order-item-price";
    price.textContent = p.linePrice || p.price || "";
    footer.appendChild(qty);
    footer.appendChild(price);
    body.appendChild(footer);

    line.appendChild(body);
    return line;
  }

  _totalRow(label, value, { muted = false, accent = false, html = false } = {}) {
    const row = document.createElement("div");
    row.className = "ws-order-total-row";
    if (muted) row.classList.add("is-muted");
    if (accent) row.classList.add("is-accent");
    const l = document.createElement("span");
    l.textContent = label;
    const v = document.createElement("span");
    if (html) v.innerHTML = value;
    else v.textContent = value;
    row.appendChild(l);
    row.appendChild(v);
    return row;
  }

  _shippingRow(summary) {
    const sel = summary?.deliveryGroups?.[0]?.selectedOption;
    const amt = sel?.estimatedCost?.amount;
    const ccy = sel?.estimatedCost?.currencyCode;
    const sym = moneySymbol(ccy);
    const shipAmt = parseFloat(amt ?? "");
    const shipLabel = sel?.title ? `Shipping (${sel.title})` : "Shipping";

    if (!Number.isNaN(shipAmt)) {
      if (shipAmt === 0) {
        return this._totalRow(shipLabel, "FREE", { accent: true });
      }
      return this._totalRow(shipLabel, `${sym}${shipAmt.toFixed(2)}`);
    }
    return this._totalRow("Shipping", "Calculated at checkout", { muted: true });
  }
}
